import { CommonButtonStyles } from '../../molecules';
import { Theme } from '../types';

const commonButtonStyles = {
    color: '#fff',
    display: 'flex',
    height: '3rem',
    padding: '1rem',
    alignItems: 'center',
    borderRadius: '8px',
    border: 'unset',
} as const satisfies CommonButtonStyles;

// Color constants
const COLOR_WHITE = '#FFFFFF';
const COLOR_BLACK = 'black';
const COLOR_PRIMARY = '#56B529';
const COLOR_PRIMARY_HOVER = '#6EC943';
const COLOR_PRIMARY_LIGHT = '#F1F7E9';
const COLOR_PRIMARY_BORDER = '#00643A';
const COLOR_PRIMARY_FOCUS = '#6180FF';

const COLOR_DISABLED_BG = '#D6D6D6';
const COLOR_DISABLED_TEXT = '#6E6E6E';

const COLOR_ERROR = '#980000';
const COLOR_ERROR_ALT = '#DC2A2A';
const COLOR_ERROR_FOCUS = '#14555A';
const COLOR_ERROR_BG_LIGHT = '#FDEEEE';

const COLOR_INPUT_DISABLED_OUTLINE = '#B3D5D5';
const COLOR_CHECKBOX_DISABLED_OUTLINE = '#D2F0E9';

const COLOR_TOGGLE_SELECTED = '#B5E099';

const COLOR_DANGER_OUTLINE = '#FDBB2F';
const COLOR_DANGER_HOVER_BG = 'rgba(0, 51, 255, 0.1)';

const secAndTertial = {
    ...commonButtonStyles,
    default: {
        outline: `1px solid ${COLOR_PRIMARY}`,
        background: COLOR_WHITE,
        color: COLOR_BLACK,
    },
    hover: {
        outline: `1px solid ${COLOR_PRIMARY_BORDER}`,
        background: COLOR_PRIMARY_LIGHT,
        color: COLOR_PRIMARY_BORDER,
    },
    focus: {
        outline: `1px solid ${COLOR_PRIMARY_FOCUS}`,
        background: COLOR_WHITE,
        color: COLOR_BLACK,
    },
    active: {
        outline: `1px solid ${COLOR_PRIMARY}`,
        background: COLOR_WHITE,
        color: COLOR_BLACK,
    },
    disabled: {
        outline: `1px solid ${COLOR_DISABLED_BG}`,
        background: COLOR_WHITE,
        color: COLOR_DISABLED_TEXT,
    },
};

export const postenBring = {
    input: {
        active: {},
        default: {
            border: 'unset',
            borderRadius: '0.5rem',
            outline: `1px ${COLOR_DISABLED_TEXT} solid`,
            color: COLOR_BLACK,
            background: COLOR_WHITE,
            padding: '0.5rem 1rem',
            fontSize: '1rem',
        },
        hover: {},
        disabled: {
            outline: `1px ${COLOR_INPUT_DISABLED_OUTLINE} solid`,
        },
        error: {
            outline: `1px ${COLOR_ERROR} solid`,
        },
        focus: {
            outline: `2px ${COLOR_PRIMARY} solid`,
        },
        placeholder: {},
    },
    toggleButton: {
        selected: {
            ...secAndTertial,
            default: {
                ...secAndTertial.default,
                background: COLOR_TOGGLE_SELECTED,
                height: 'unset',
                outline: `1px solid ${COLOR_TOGGLE_SELECTED}`,
            },
            error: { background: COLOR_ERROR_ALT, color: COLOR_WHITE },
        },
        unselected: {
            ...secAndTertial,
            default: {
                ...secAndTertial.default,
                height: 'unset',
            },
            error: { background: COLOR_ERROR_ALT, color: COLOR_WHITE },
        },
    },
    progressDots: {
        color: COLOR_PRIMARY,
    },
    button: {
        primary: {
            ...commonButtonStyles,
            default: {
                background: COLOR_PRIMARY,
                color: COLOR_BLACK,
            },
            hover: {
                background: COLOR_PRIMARY_HOVER,
            },
            focus: {
                background: COLOR_PRIMARY,
            },
            active: {},
            disabled: {
                background: COLOR_DISABLED_BG,
                color: COLOR_DISABLED_TEXT,
            },
        },
        secondary: secAndTertial,
        ghost: {
            ...secAndTertial,
            default: {
                color: COLOR_BLACK,
                textDecoration: 'underline',
                textDecorationColor: COLOR_PRIMARY,
                textDecorationThickness: '1px',
                textUnderlineOffset: '4px',
            },
            hover: {},
        },
        tertiary: secAndTertial,
        danger: {
            ...commonButtonStyles,
            default: {
                outline: `1px solid ${COLOR_DANGER_OUTLINE}`,
                background: 'transparent',
            },
            hover: {
                outline: `1px solid ${COLOR_DANGER_OUTLINE}`,
                background: COLOR_DANGER_HOVER_BG,
            },
            focus: {
                outline: `4px solid ${COLOR_DANGER_OUTLINE}`,
            },
            active: {
                outline: `1px solid ${COLOR_DANGER_OUTLINE}`,
                background: COLOR_WHITE,
            },
            disabled: {
                background: COLOR_DISABLED_BG,
                color: COLOR_DISABLED_TEXT,
            },
        },
    },
    checkbox: {
        unselected: {
            active: {},
            default: {
                background: COLOR_WHITE,
                outline: `0.125rem solid ${COLOR_DISABLED_TEXT}`,
            },
            svg: {
                stroke: 'transparent',
            },
            hover: {},
            focus: {},
            disabled: {
                outline: `0.125rem solid ${COLOR_CHECKBOX_DISABLED_OUTLINE}`,
            },
            error: {
                outline: `0.125rem solid ${COLOR_ERROR_ALT}`,
                background: COLOR_WHITE,
                hover: {
                    outline: `0.125rem solid ${COLOR_ERROR_ALT}`,
                    background: COLOR_ERROR_BG_LIGHT,
                },
                focus: {
                    outline: `0.125rem solid ${COLOR_ERROR_FOCUS}`,
                    background: COLOR_ERROR_BG_LIGHT,
                },
            },
        },
        selected: {
            active: {},
            default: {
                background: COLOR_PRIMARY,
                outline: `0.125rem solid ${COLOR_PRIMARY}`,
            },
            svg: {
                stroke: COLOR_WHITE,
            },
            hover: {},
            focus: {},
            disabled: {
                outline: `0.125rem solid ${COLOR_CHECKBOX_DISABLED_OUTLINE}`,
            },
            error: {
                outline: `0.125rem solid ${COLOR_ERROR_ALT}`,
                background: COLOR_WHITE,
                svg: {
                    stroke: COLOR_ERROR_ALT,
                },
                hover: {
                    outline: `0.125rem solid ${COLOR_ERROR_ALT}`,
                    background: COLOR_ERROR_BG_LIGHT,
                },
                focus: {
                    outline: `0.125rem solid ${COLOR_ERROR_FOCUS}`,
                    background: COLOR_ERROR_BG_LIGHT,
                },
            },
        },
    },
    texts: {
        links: {
            default: {
                textDecorationColor: COLOR_PRIMARY,
                color: COLOR_BLACK,
                textUnderlineOffset: '4px',
            },
            visited: {
                default: {},
                active: {},
                hover: {
                    color: COLOR_PRIMARY_BORDER,
                },
                focus: {},
            },
            active: {},
            hover: {
                color: COLOR_PRIMARY_BORDER,
            },
            focus: {},
        },
        error: {
            default: {
                color: COLOR_ERROR_ALT,
            },
        },
        default: {
            background: COLOR_WHITE,
        },
        typography: {
            defaults: {
                color: 'unset',
                weights: {
                    medium: {
                        fontWeight: 500,
                    },
                    bold: {
                        fontWeight: 700,
                    },
                    regular: {
                        fontWeight: 400,
                    },
                },
            },
            button: {
                fontSize: '1rem',
                lineHeight: '1.5rem',
                fontWeight: 400,
            },
            label: {
                fontSize: '1.25rem',
                lineHeight: '1.125rem',
                fontWeight: 400,
            },
            body: {
                mobile: {
                    lineHeight: '1.75rem',
                    fontSize: '1.25rem',
                },
                lineHeight: '1.75rem',
                fontSize: '1.25rem',
            },
            body2: {
                fontWeight: 400,
                lineHeight: '1.625rem',
                fontSize: '1.125rem',
                mobile: {
                    lineHeight: '1.625rem',
                    fontSize: '1.125rem',
                },
            },
            h3: {
                fontFamily: 'PostenSansMedium',
                fontSize: '2rem',
                lineHeight: '2.5rem',
                mobile: {
                    fontSize: '2rem',
                    lineHeight: '2.5rem',
                },
            },
            h4: {
                fontFamily: 'PostenSansMedium',
                fontSize: '1.4375rem',
                fontWeight: 400,
                lineHeight: '2rem',
                mobile: {
                    fontSize: '1.4375rem',
                    lineHeight: '2rem',
                },
            },
        },
    },
} satisfies Theme;
